import { Autocomplete, TextField, Select, MenuItem, FormControl } from "@mui/material";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { fileTypeFromBuffer } from "file-type";
import MaskedInput from "react-text-mask";
import { toast } from "react-toastify";
import { Tooltip } from "antd";
import i18next from "i18next";
import axios from "axios";

import {
  addReportEntry,
  deleteReportEntry,
  downloadFile,
  fetchReportEntryById,
  updateReportEntry,
  useReportEntries,
} from "../service/reportEntry";
import { fetchClients, fetchCostCenters } from "../service/costCenter";
import { formatCurrency } from "../utils/maskFormatMoney";
import { ReportEntry } from "../@types/ReportEntry";
import { Subcategory } from "../enums/Subcategory";
import Collaborator from "../@types/Collaborator";
import CostCenter from "../@types/CostCenter";
import FormPage from "../templates/FormPage";
import { Category } from "../enums/Category";
import { Status } from "../enums/Status";
import Loading from "./atoms/Loading";
import Modal from "./atoms/Modal";

interface ReportEntryFormProps {
  account: Collaborator;
  initialReportEntry?: ReportEntry;
}

const ReportEntryForm: FC<ReportEntryFormProps> = ({ account, initialReportEntry }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    formState: { errors },
  } = useForm<ReportEntry>({
    defaultValues: {},
  });
  const [clients, setClients] = useState<string[]>([]);
  const [reportEntry, setReportEntry] = useState<ReportEntry>();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isOpenDelete, setIsOpenDelete] = useState<boolean>(false);

  const { data: reportEntries } = useReportEntries(
    0,
    1,
    {
      collaboratorName: "",
      collaborator: account,
      ownerName: "",
      costCenter: "",
      statusFilter: null,
      startDate: null,
      endDate: null,
      expenseCategory: null,
      ownerApproved: undefined,
      financialApproved: undefined,
      ownerApprovedDateUntil: null,
      ownerApprovedDateAfter: null,
      financialApproveDateUntil: null,
      financialApproveDateAfter: null,
      createDateUntil: null,
      createDateAfter: null,
    },
    true,
    "createdDate,desc",
  );

  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get("id");
  const watchCostCenterClient = watch("costCenter.client", "");
  const watchExpendCategory = watch("expendCategory", "");
  const watchCostCenterId = watch("costCenter.id");
  const watchWarnings = watch("warnings", [""]);

  const defaultMaskOptions = {
    prefix: "R$ ",
    suffix: "",
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ".",
    allowDecimal: true,
    decimalSymbol: ",",
    decimalLimit: 2,
    integerLimit: 12,
    allowNegative: true,
    allowLeadingZeroes: false,
  };
  const mask = createNumberMask(defaultMaskOptions);

  const handleDocumentClick = async (reportEntry: ReportEntry) => {
    const response = await downloadFile(reportEntry.id);

    const url = window.URL.createObjectURL(response);
    const link = document.createElement("a");
    link.href = url;

    let fileName = reportEntry.s3FileName;
    if (response.headers) {
      const contentDisposition = response.headers["content-disposition"];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }
    }
    link.setAttribute("download", fileName);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  const parseCurrency = (value: string | number): number => {
    if (typeof value === "number") return value;
    if (!value) return 0.0;

    const cleanString = value.replace(/[^\d,-]/g, "").replace(",", ".");

    return parseFloat(cleanString);
  };

  const mutation = useMutation(
    async (data: FieldValues) => {
      const costCenter = {
        id: data.costCenter.id,
      };
      const collaborator = {
        id: account.id,
      };

      delete data.proof;
      const mappedData = {
        ...data,
        value: parseCurrency(data.value),
        costCenter,
        collaborator,
        isAdvance: false,
      };

      if (id) {
        return updateReportEntry(mappedData as ReportEntry, attachedFiles);
      }
      return addReportEntry(mappedData as ReportEntry, attachedFiles);
    },
    {
      onSuccess: async (result) => {
        await queryClient.invalidateQueries(["reportEntries"]);
        if (result.warnings && result.warnings.length > 0) {
          toast.warning(result.warnings[0], {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
        toast.success("Sucesso ao realizar ação de criação!");
        navigate("/");
      },
      onError: (error: Error) => {
        console.error("Erro na mutação:", error.message);
        if (axios.isAxiosError(error) && error.response?.status === 413) {
          toast.error(i18next.t("error413"), {
            position: toast.POSITION.TOP_RIGHT,
          });
        } else {
          toast.error(error.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      },
    },
  );

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    mutation.mutate(data);
  };

  const handleCategoryChange = (e: string) => {
    setValue("expendCategory", e);
    if (e !== "FOOD") {
      setValue("expendSubcategory", "");
    }
  };

  const handleFetchReportEntryData = useCallback(
    async (id: number) => {
      fetchReportEntryById(id).then(async (reportEntryData) => {
        setReportEntry(reportEntryData);
        Object.entries(reportEntryData).forEach(([key, value]) => {
          if (key === "value") {
            setValue(key as keyof ReportEntry, formatCurrency(value));
          } else {
            setValue(key as keyof ReportEntry, value);
          }
        });
        setValue("costCenter.client", reportEntryData.costCenter.client);
        setValue("costCenter.name", reportEntryData.costCenter.name);

        setDataLoaded(true);
      });
    },
    [setValue],
  );

  const deleteReportEntryMutation = useMutation((reportEntryId: number) => deleteReportEntry(reportEntryId), {
    onSuccess: () => {
      queryClient.invalidateQueries(["reportEntries"]);
      toast.success("Sucesso ao realizar ação de exclusão!");
      navigate("/");
    },
    onError: (error: Error) => {
      console.error("Erro ao excluir entrada de relatório:", error);
      toast.error(error?.message);
    },
  });

  const handleDeleteClick = (reportEntryId: number) => {
    deleteReportEntryMutation.mutate(reportEntryId);
  };

  useEffect(() => {
    if (!id) {
      reset({});
      setValue("costCenter.client", "");
      setValue("costCenter.id", undefined as unknown as number);
      setValue("expendCategory", "");
      setAttachedFiles([]);
    }
  }, [id, reset]);

  useEffect(() => {
    if (id && initialReportEntry) {
      reset(initialReportEntry);
    }
  }, [id, initialReportEntry, reset]);

  useEffect(() => {
    return () => {
      reset({});
      setValue("costCenter.client", "");
      setValue("costCenter.id", undefined as unknown as number);
      setValue("expendCategory", "");
      setAttachedFiles([]);
    };
  }, [reset]);

  useEffect(() => {
    fetchClients().then((data) => {
      setClients(data);
    });
  }, []);

  useEffect(() => {
    if (!id && reportEntries && reportEntries?.content?.length > 0) {
      const latestEntry = reportEntries.content[0];

      if (!watchCostCenterClient && latestEntry.costCenter?.client) {
        setValue("costCenter.client", latestEntry.costCenter.client);
      }

      if (!watchCostCenterId && latestEntry.costCenter?.id) {
        setValue("costCenter.id", Number(latestEntry.costCenter.id));
      }

      if (!watchExpendCategory && latestEntry.expendCategory) {
        setValue("expendCategory", latestEntry.expendCategory);
      }
    }
  }, [reportEntries, id]);

  useEffect(() => {
    if (watchCostCenterClient) {
      setLoading(true);
      fetchCostCenters(watchCostCenterClient)
        .then((data) => {
          setCostCenters(data);
        })
        .catch((error) => {
          console.error("Error fetching projects:", error);
          toast.error(error.message, {
            position: toast.POSITION.TOP_CENTER,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [watchCostCenterClient]);

  useEffect(() => {
    if (id && clients.length > 0) {
      handleFetchReportEntryData(Number(id));
    }
  }, [clients, handleFetchReportEntryData, id]);

  return (
    <FormPage>
      <h1 className="text-vcolor-hover p-5 underline">{id ? "Editar Solicitação" : "Nova Solicitação"}</h1>

      {id && watchWarnings && watchWarnings.length > 0 && (
        <div className="flex justify-center items-center pb-2">
          <div className="flex items-center w-1/2 laptop-max:w-full max-w-screen-md">
            <div className="flex w-full p-4 mt-4 text-yellow-800 bg-yellow-200 border border-yellow-400 rounded">
              {watchWarnings}
            </div>
          </div>
        </div>
      )}

      {id && !dataLoaded ? (
        <Loading />
      ) : (
        <div className="flex justify-center items-center pb-2">
          <form
            className="flex flex-col items-center w-1/2 my-4 laptop-max:w-full max-w-screen-md overflow-hidden"
            onSubmit={handleSubmit(onSubmit)}
            encType="multipart/form-data"
          >
            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-1/2 pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="data"
                  className={`block ${
                    errors.expenseDate ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.date")}
                  <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    {...register("expenseDate", { required: true })}
                    className={`w-full h-[36px] border transition-colors duration-200 ${
                      errors.expenseDate
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500"
                    } text-gray-700 rounded-lg block text-base px-2 py-1 dark:bg-white dark:text-gray-600 
                    hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1`}
                  />
                  {errors.expenseDate && (
                    <p className="mt-1 text-sm text-left text-red-500">{i18next.t("form.requiredField")}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pl-0">
                <label
                  htmlFor="valor"
                  className={`block ${
                    errors.value ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.value")}
                  <span className="text-red-500">*</span>
                </label>
                <div>
                  <MaskedInput
                    mask={mask}
                    type={"text"}
                    defaultValue={watch("value") || ""}
                    className={`w-full h-[36px] bg-white p-1 border transition-colors duration-200 ${
                      errors.value
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500"
                    } text-gray-700 text-base rounded-lg block pl-4 dark:bg-white dark:text-gray-600
                    hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2`}
                    {...register("value", {
                      required: true,
                      validate: (value) => {
                        const stringValue = String(value);
                        const numericValue = parseFloat(
                          stringValue
                            .replace(/[^0-9.,-]/g, "")
                            .replace(/\./g, "")
                            .replace(/,/g, "."),
                        );
                        return !isNaN(numericValue) || "Valor inválido";
                      },
                    })}
                    onChange={(e) => {
                      setValue(
                        "value",
                        parseFloat(
                          e.target.value
                            .replace(/[^0-9.,-]/g, "")
                            .replace(/\./g, "")
                            .replace(/,/g, "."),
                        ),
                      );
                    }}
                  />
                  {errors.value && <p className="mt-1 text-sm text-left text-red-500">Valor inválido!</p>}
                </div>
              </div>
            </div>

            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-1/2 pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="cliente"
                  className={`block ${
                    errors.costCenter?.id && !watchCostCenterClient ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.client")}
                  <span className="text-red-500">*</span>
                </label>
                <Autocomplete
                  id="client"
                  options={clients}
                  value={watchCostCenterClient || ""}
                  onChange={(_, newValue) => {
                    setValue("costCenter.client", newValue || "");

                    if (!newValue) {
                      setValue("costCenter.id", undefined as unknown as number);
                      setValue("costCenter.name", "");
                      setCostCenters([]);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!errors.costCenter?.id}
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "36px",
                          "& input": {
                            padding: "6px 12px",
                            height: "20px",
                          },
                        },
                      }}
                      className={`w-full border ${
                        errors.costCenter?.id && !watchCostCenterClient
                          ? "border-red-500"
                          : "border-gray-300 dark:border-gray-400"
                      } text-gray-500 text-sm rounded-md block dark:bg-white dark:text-gray-600`}
                    />
                  )}
                />
              </div>

              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pl-0">
                <label
                  htmlFor="projeto"
                  className={`block ${
                    errors.costCenter?.id && !watchCostCenterId ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.project")}
                  <span className="text-red-500">*</span>
                </label>
                <Controller
                  name="costCenter.id"
                  control={control}
                  defaultValue={watchCostCenterId}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Autocomplete
                      id="project"
                      options={loading ? [] : costCenters}
                      getOptionLabel={(option) => option.name}
                      value={costCenters.find((costCenter) => costCenter.id === field.value) || null}
                      onChange={(_event, newValue) => field.onChange(newValue ? newValue.id : null)}
                      slotProps={{
                        paper: {
                          sx: {
                            "& .MuiAutocomplete-option": {
                              textAlign: "left",
                            },
                          },
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          error={!!errors.costCenter?.id}
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "36px",
                              "& input": {
                                padding: "6px 12px",
                                height: "20px",
                              },
                            },
                          }}
                          className={`w-full border ${
                            errors.costCenter?.id && !field.value
                              ? "border-red-500"
                              : "border-gray-300 dark:border-gray-400"
                          } text-gray-500 text-sm rounded-md block dark:bg-white dark:text-gray-600`}
                        />
                      )}
                    />
                  )}
                />
              </div>
            </div>

            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col laptop-max:mb-0 items-center">
              <div className="flex flex-col w-1/2 pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="categoria"
                  className={`block ${
                    errors.expendCategory ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.category")}
                  <span className="text-red-500">*</span>
                </label>
                <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}>
                  <Select
                    id="expendCategory"
                    value={watchExpendCategory || ""}
                    {...register("expendCategory", { required: true })}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    error={!!errors.expendCategory}
                    className="bg-white"
                    sx={{
                      height: "36px",
                      "& .MuiSelect-select": {
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        padding: "0px 12px",
                      },
                    }}
                  >
                    <MenuItem value="">- Selecione uma Opção -</MenuItem>
                    {Object.values(Category)
                      .filter((category) => category !== Category.advance)
                      .map((categoryValue) => (
                        <MenuItem key={categoryValue} value={categoryValue}>
                          {i18next.t(`category.${categoryValue}`)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>

              <div className="flex flex-col w-1/2 laptop-max:w-[80%] laptop-max:pl-0">
                <label
                  htmlFor="subcategoria"
                  className={`block ${
                    errors.expendSubcategory ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.sub_category")}
                  {(watchExpendCategory == "FOOD" || watchExpendCategory === "FOOD") && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <FormControl fullWidth size="small">
                  <Select
                    id="expendSubcategory"
                    value={watch("expendSubcategory") || ""}
                    {...register("expendSubcategory", {
                      required: watchExpendCategory === "FOOD" || watchExpendCategory === "FOOD",
                    })}
                    error={!!errors.expendSubcategory}
                    disabled={
                      (!watchExpendCategory && watchExpendCategory !== "FOOD") ||
                      (watchExpendCategory !== "FOOD" && watchExpendCategory !== "")
                    }
                    className="bg-white"
                    sx={{
                      height: "36px",
                      "& .MuiSelect-select": {
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        padding: "0px 12px",
                      },
                    }}
                  >
                    <MenuItem value="">- Selecione uma Opção -</MenuItem>
                    {(watchExpendCategory === "FOOD" || watchExpendCategory === "FOOD") &&
                      Object.values(Subcategory).map((subCategoryValue) => (
                        <MenuItem key={subCategoryValue} value={subCategoryValue}>
                          {i18next.t(`sub_category.${subCategoryValue}`)}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="flex flex-row w-full mb-4 gap-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-1/2 pr-2 justify-start laptop-max:w-[80%] laptop-max:pl-0 laptop-max:mt-8">
                <label
                  htmlFor="valor"
                  className={`block ${
                    errors.proof ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.receipt")}
                  <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-row w-full laptop-max:flex-col items-center gap-2">
                  <input
                    value={attachedFiles[0]?.name || reportEntry?.s3FileName || ""}
                    className={`w-[80%] laptop-max:w-full h-[36px] bg-white border ${
                      errors.proof ? "border-red-500" : "border-gray-300 dark:border-gray-400"
                    } text-gray-500 text-sm rounded-md block py-1 px-3 dark:text-gray-600`}
                    disabled
                  />
                  <div className="flex flex-row justify-center items-center w-[20%] laptop-max:w-full">
                    <Tooltip title={i18next.t("form.attach_receipt")} placement="top">
                      <label
                        htmlFor="proof"
                        className="w-full flex items-center justify-center bg-color-text text-white text-sm py-1 px-4 rounded-md hover:bg-vcolor-hover file-upload-button relative"
                      >
                        <input
                          type="file"
                          id="proof"
                          accept=".jpg,.jpeg,.png,.pdf"
                          {...register("proof")} // colocar de volta  { required: !id }
                          onChange={async (e) => {
                            if (e.target.files) {
                              const file = e.target.files[0];

                              const uint8Array = new Uint8Array(await file.arrayBuffer());

                              const type = await fileTypeFromBuffer(uint8Array);

                              const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf"];
                              const allowedMimeTypes = ["image/jpeg", "image/png", "application/pdf"];

                              const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
                              const isValidExtension = allowedExtensions.includes(fileExtension);
                              const isValidMimeType = allowedMimeTypes.includes(type?.mime || file?.type);

                              if (!isValidExtension || !isValidMimeType) {
                                toast.error("Apenas arquivos JPG, JPEG, PNG e PDF são permitidos", {
                                  position: toast.POSITION.TOP_RIGHT,
                                });

                                e.target.value = "";
                                return;
                              }

                              if (file.size > 3 * 1024 * 1024) {
                                toast.error("O tamanho máximo de anexo é 3mb", {
                                  position: toast.POSITION.TOP_RIGHT,
                                });
                                e.target.value = "";
                                return;
                              }

                              if (attachedFiles.length === 0)
                                setAttachedFiles([...attachedFiles, ...Array.from(e.target.files)]);
                              else setAttachedFiles([file]);
                            }
                          }}
                          className="hidden"
                        />
                        <span className="flex button-style">
                          <ArrowUpTrayIcon className="w-5 h-6" />
                        </span>
                      </label>
                    </Tooltip>
                  </div>
                  {reportEntry && (
                    <div className="flex flex-row justify-center items-center w-[12%] laptop-max:w-full">
                      <Tooltip title={i18next.t("form.download_receipt")} placement="top">
                        <button
                          type="button"
                          onClick={() => {
                            if (reportEntry) {
                              handleDocumentClick(reportEntry);
                            }
                          }}
                          className="flex w-full h-8 bg-vcolor text-white items-center justify-center rounded-md drop-shadow-lg hover:bg-vcolor-hover"
                        >
                          <ArrowDownTrayIcon className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col w-1/2 pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="refund_client"
                  className="block text-color-default-gray text-sm font-semibold text-left mb-1"
                >
                  {i18next.t("form.refund_client")}
                </label>
                <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { height: "40px" } }}>
                  <Select
                    id="refund_client"
                    value={watch("clientRefund")?.toString() || "false"}
                    {...register("clientRefund")}
                    sx={{
                      height: "36px",
                      "& .MuiSelect-select": {
                        height: "36px",
                        display: "flex",
                        alignItems: "center",
                        textAlign: "left",
                        padding: "0px 12px",
                      },
                    }}
                  >
                    <MenuItem value="false">Não</MenuItem>
                    <MenuItem value="true">Sim</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <div className="flex flex-col w-full mb-4 laptop-max:flex-col items-center">
              <div className="flex flex-col w-full pr-2 laptop-max:w-[80%] laptop-max:pr-0">
                <label
                  htmlFor="comentarios"
                  className={`block ${
                    errors.description ? "text-red-500" : "text-color-default-gray"
                  } text-sm font-semibold text-left mb-1`}
                >
                  {i18next.t("form.comments")}
                </label>
                <textarea
                  id="description"
                  {...register("description")}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-md transition-colors duration-200 ${
                    errors.description
                      ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 dark:border-gray-400 focus:ring-blue-500 focus:border-blue-500"
                  } bg-white text-base
                  hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2`}
                />
              </div>
            </div>

            <div className="flex flex-row w-full gap-2 justify-end laptop-max:flex-col laptop-max:items-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={mutation.isLoading}
                className="w-[20%] laptop-max:w-[80%] h-10 border border-vcolor bg-white text-vcolor rounded-md 
                transition-colors duration-150 ease-in-out hover:bg-vcolor/10
                focus:outline-none focus:ring-1 focus:ring-vcolor focus:ring-opacity-30
                disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {i18next.t("back")}
              </button>
              {id &&
                (reportEntry?.reportEntryStatus == Status.ON_APPROVAL ||
                  reportEntry?.reportEntryStatus == Status.REFUSED) && (
                  <button
                    type="button"
                    onClick={() => setIsOpenDelete(true)}
                    disabled={mutation.isLoading}
                    className="w-[25%] laptop-max:w-[80%] h-10 border border-red-900 bg-white text-red-900 rounded-md
                    transition-colors duration-150 ease-in-out hover:bg-red-900/10
                    focus:outline-none focus:ring-1 focus:ring-red-900 focus:ring-opacity-30
                    disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {i18next.t("delete")}
                  </button>
                )}
              {(reportEntry?.reportEntryStatus == Status.ON_APPROVAL ||
                reportEntry?.reportEntryStatus == Status.REFUSED ||
                !reportEntry) && (
                <button
                  type="submit"
                  disabled={mutation.isLoading}
                  className="w-[20%] laptop-max:w-[80%] h-10 bg-vcolor text-white rounded-md
                  transition-colors duration-150 ease-in-out hover:bg-vcolor/90
                  focus:outline-none focus:ring-1 focus:ring-vcolor focus:ring-opacity-30
                  disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mutation.isLoading ? `${i18next.t("saving")}` : `${i18next.t("send")}`}
                </button>
              )}
            </div>
          </form>
        </div>
      )}
      <Modal
        title="Atenção"
        isOpen={isOpenDelete}
        setIsOpen={setIsOpenDelete}
        body={
          <div className="mt-2">
            <p className="text-sm text-gray-500">Você tem certeza que deseja deletar essa solicitação ?</p>
          </div>
        }
        bottom={
          <div className="flex flex-row justify-around mt-4">
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-green-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => {
                handleDeleteClick(reportEntry?.id as number);
                setIsOpenDelete(false);
              }}
            >
              Sim
            </button>
            <button
              type="button"
              className="inline-flex justify-center rounded-md border border-transparent bg-red-400 px-4 py-2 text-sm font-medium text-white hover:bg-vcolor focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
              onClick={() => setIsOpenDelete(false)}
            >
              Não
            </button>
          </div>
        }
      />
    </FormPage>
  );
};

export default ReportEntryForm;
